// @ts-check
import Text from 'components/base/Text';
import { Flex, Padding } from 'components/base/layout-components';
import { verticalScroller } from 'components/base/scroller';
import React from 'react';
import { createToken } from 'common/services/api-tokens.service';
import HideShowCopyTextField from 'components/HideShowCopyTextField/HideShowCopyTextField';

import styled from 'styled-components';
import { TextField } from '@material-ui/core';
import _ from 'lodash';
import { getColor } from 'common/constants/_colors';
import * as TrackingService from 'common/services/tracking.service';
import { TRACKING } from 'common/constants/constants';
import { LBButton } from '@linearb/buzz';
const ERROR_MESSAGE = 'Oops! There was an issue creating token';
const PRIVACY_POLICY_LINK = 'https://linearb.io/privacy-policy/';
const TERMS_LINK = 'https://linearb.io/services-agreement/';

const TextFieldWrapper = styled(Padding)`
  border-radius: 4px;
  border: 1px solid ${getColor('botticelli')};
  margin-top: 0.4rem;
`;

const Title = ({ text, ...props }) => {
  return (
    <Flex items='center' {...props}>
      <Text size='x-large'>{text}</Text>
    </Flex>
  );
};
const LABEL_REGEX_VALIDATOR = new RegExp('^$|^[A-Za-z0-9 ,_-]+$');

const ScrolledContainer = styled(Flex)`
  ${verticalScroller()};
  max-height: 90rem;
`;

const LinkifiedText = styled.span`
  color: ${getColor('chambray')};
  cursor: pointer;
`;

const openPrivacyPolicy = () => {
  window.open(PRIVACY_POLICY_LINK);
};
const openTerms = () => {
  window.open(TERMS_LINK);
};

const CreateTokenScreen = ({ onClickCreateToken, label, setLabel }) => {
  return (
    <ScrolledContainer direction='column' padding='medium'>
      <Text color='shuttleGray' size='medium'>
        Choose a label that is short and easy for you to remember:
      </Text>
      <Padding t='medium'>
        <Text color='shuttleGray' size='medium'>
          Label:
        </Text>
        <TextFieldWrapper padding='x-small'>
          <TextField
            value={label}
            data-cy='api-token-label-textfield-data-cy'
            onChange={e =>
              LABEL_REGEX_VALIDATOR.test(e.target.value) &&
              setLabel(e.target.value)
            }
            InputProps={{
              inputProps: {
                'data-cy': 'api-token-label-input-data-cy',
                style: {
                  fontSize: 14
                }
              },
              disableUnderline: true
            }}
          />
        </TextFieldWrapper>
        <Padding t='medium' b='medium'>
          <Text size='small' color='shuttleGray'>
            by creating this token, you agree to LinearB’s{' '}
            <LinkifiedText onClick={openTerms}>terms</LinkifiedText> and{' '}
            <LinkifiedText onClick={openPrivacyPolicy}>
              privacy policy
            </LinkifiedText>
          </Text>
        </Padding>
        <Flex justify='flex-end'>
          <LBButton
            data-cy='create-api-token-button-modal-data-cy'
            onClick={onClickCreateToken}
            disabled={label.trim() === ''}
          >
            Create
          </LBButton>
        </Flex>
      </Padding>
    </ScrolledContainer>
  );
};
const ViewTokenScreen = ({
  generatedToken,
  toggleSnackBar,
  showGoToSettingsMessage,
  onGoToSettingsClicked
}) => {
  const { original_token: originalToken } = generatedToken;
  return (
    <ScrolledContainer direction='column' padding='medium'>
      <Text color='shuttleGray' size='medium'>
        Make sure to copy your new token.
        <br /> You won’t be able to see this token again.
      </Text>

      <Padding t='medium' b='medium'>
        <Text color='shuttleGray' size='medium'>
          Token:
        </Text>
        <HideShowCopyTextField
          value={originalToken}
          showVisibilityToggle={false}
          initiallyVisible={true}
          toggleSnackBar={toggleSnackBar}
        />
        {showGoToSettingsMessage && onGoToSettingsClicked && (
          <Padding t='medium' b='medium'>
            <Text size='small' color='shuttleGray'>
              See your list of tokens on:{' '}
              <LinkifiedText>Settings / Api tokens</LinkifiedText>
            </Text>
          </Padding>
        )}
      </Padding>
    </ScrolledContainer>
  );
};

const CreateApiTokenModal = ({
  toggleSnackBar,
  apiTokens,
  setApiTokens,
  showGoToSettingsMessage,
  onGoToSettingsClicked,
  onTokenGenerated,
  account
}) => {
  const [label, setLabel] = React.useState('API Integration');
  const [generatedToken, setGeneratedToken] = React.useState(null);

  const onClickCreateToken = async () => {
    TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
      page: window.location.pathname,
      click_type: TRACKING.CLICK.CREATE_API_TOKEN,
      token_label: label
    });
    try {
      const response = await createToken({ label });
      const originalToken = _.get(response, 'original_token');
      const token = _.get(response, 'data');
      if (!originalToken || !token) {
        throw new Error('No token was generated');
      }
      onTokenGenerated && onTokenGenerated(token);
      setApiTokens([
        { ...token, account_name: _.get(account, 'name') },
        ...apiTokens
      ]);
      setGeneratedToken({ ...token, original_token: originalToken });
    } catch (exp) {
      toggleSnackBar({
        message: (
          <Text size='large' color='white'>
            {ERROR_MESSAGE}
          </Text>
        ),
        variant: 'error'
      });
    }
  };
  return (
    <Flex direction='column'>
      <Title
        padding='medium'
        pb='no-space'
        text={generatedToken ? 'Your new API token' : 'Create an API token'}
      />
      {generatedToken ? (
        <ViewTokenScreen
          generatedToken={generatedToken}
          toggleSnackBar={toggleSnackBar}
          showGoToSettingsMessage={showGoToSettingsMessage}
          onGoToSettingsClicked={onGoToSettingsClicked}
        />
      ) : (
        <CreateTokenScreen
          label={label}
          setLabel={setLabel}
          onClickCreateToken={onClickCreateToken}
        />
      )}
    </Flex>
  );
};

export default CreateApiTokenModal;
